import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Download,
  Settings,
  Plus,
  Upload,
  Layers,
  Palette,
  Sliders,
  Sparkles,
  Undo2,
  Share2,
  Box,
  Eye,
  ChevronDown,
  Users,
  Mountain,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DropZone } from "@/components/height-map/DropZone";
import { PaletteStrip } from "@/components/height-map/PaletteStrip";
import { HeightLayerRow } from "@/components/height-map/HeightLayerRow";
import { HowItWorks } from "@/components/height-map/HowItWorks";
import { HelpDialog } from "@/components/height-map/HelpDialog";
import { PresetsDialog } from "@/components/height-map/PresetsDialog";
import { SettingsDialog } from "@/components/height-map/SettingsDialog";
import { PaintCanvas } from "@/components/height-map/PaintCanvas";
import { AdvancedMode } from "@/pages/AdvancedMode";
import { Terrain3D } from "@/components/height-map/Terrain3D";
import { ColorMapImportDialog } from "@/components/height-map/ColorMapImportDialog";
import { PhotoshopLayout } from "@/components/height-map/PhotoshopLayout";
import { AdvancedModeNotice } from "@/components/height-map/AdvancedModeNotice";
import { SlopeControls } from "@/components/height-map/SlopeControls";
import { buildDefaultLayers } from "@/lib/palettes";
import {
  buildLUT,
  renderColorMap,
  computeHistogram,
  computeCoverage,
  computeLayerPercentages,
  buildLayerIndexLUT,
  getCombinedMaterialDescriptors,
  imageDataToCanvas,
  generateSampleHeightMap,
  computeSlopeAngles,
  applySlopeMaterial,
} from "@/lib/heightMapUtils";
import { useUndoManager, useKeyboardUndo } from "@/hooks/useUndo";

const PRESET_STORAGE_KEY = "hmcg.presets.v1";
const SETTINGS_STORAGE_KEY = "hmcg.settings.v1";
const PATTERN_STORAGE_KEY = "hmcg.patterns.v1";
const ROBLOX_PRESET_ID = "builtin_roblox";
const DEFAULT_SLOPE_SETTINGS = {
  angle: 35,
  comparison: "gte",
  materialId: null,
  protectedMaterialIds: [],
};

const DEFAULT_SETTINGS = {
  bigViewports: false,
  showHow: true,
  layerListHeight: 380,
  showStrip: true,
  showImportColorMap: false,
  originalUI: false,
  roundedMapPreview: false,
};

const HeightMapGenerator = () => {
  const [mode, setMode] = useState("gradient");
  const [layers, setLayers] = useState(() => buildDefaultLayers());
  const [allSolid, setAllSolid] = useState(true);
  const [imageMeta, setImageMeta] = useState(null);
  const [hasImage, setHasImage] = useState(false);
  const [presets, setPresets] = useState(() => loadPresets());
  const [activePresetId, setActivePresetId] = useState(ROBLOX_PRESET_ID);
  const [showHelp, setShowHelp] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [show3D, setShow3D] = useState(false);
  const [showColorMapImport, setShowColorMapImport] = useState(false);
  const [showAdvancedNotice, setShowAdvancedNotice] = useState(false);
  const [advancedNoticeAcknowledged, setAdvancedNoticeAcknowledged] = useState(false);
  const [settings, setSettings] = useState(() => loadSettings());
  const [customPatterns, setCustomPatterns] = useState(() => loadPatterns());
  const [histogram, setHistogram] = useState(new Uint32Array(256));
  const [imageVersion, setImageVersion] = useState(0);
  const [activePaintLayerId, setActivePaintLayerId] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [metric, setMetric] = useState("map"); // "map" | "layer"
  const [baseImageData, setBaseImageData] = useState(null);
  const [exactColorImageData, setExactColorImageData] = useState(null);
  const [terrainColorImageData, setTerrainColorImageData] = useState(null);
  const [paintOverlay, setPaintOverlay] = useState(null);
  const [paintSyncToken, setPaintSyncToken] = useState(0);
  const [showReference, setShowReference] = useState(false);
  const [referenceOpacity, setReferenceOpacity] = useState(0.4);
  const [undoTick, setUndoTick] = useState(0);
  const [slopeSettings, setSlopeSettings] = useState(DEFAULT_SLOPE_SETTINGS);

  const originalCanvasRef = useRef(null);
  const colorCanvasRef = useRef(null);
  const paintCanvasRef = useRef(null);
  const sourceImageDataRef = useRef(null);
  const importInputRef = useRef(null);
  const pendingImageRef = useRef(null);
  const layerDebounceRef = useRef(null);
  const prevLayersRef = useRef(null);
  const initialLoadRef = useRef(true);

  const layersUndo = useUndoManager({ maxDepth: 50 });
  const paintUndo = useUndoManager({ maxDepth: 20 });

  useEffect(() => {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);
  useEffect(() => {
    if (settings.originalUI) {
      document.documentElement.removeAttribute("google-side-rail-overlap");
    } else {
      document.documentElement.setAttribute("google-side-rail-overlap", "false");
    }
  }, [settings.originalUI]);
  useEffect(() => {
    localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(presets));
  }, [presets]);
  useEffect(() => {
    localStorage.setItem(PATTERN_STORAGE_KEY, JSON.stringify(customPatterns));
  }, [customPatterns]);

  const savePattern = (pattern) =>
    setCustomPatterns((prev) => [...prev, pattern]);
  const deletePattern = (id) =>
    setCustomPatterns((prev) => prev.filter((p) => p.id !== id));

  // Load palette from URL on first mount (?p=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get("p");
    if (!encoded) return;
    try {
      const json = JSON.parse(atob(encoded));
      if (Array.isArray(json.layers) && json.layers.length > 0) {
        const sanitized = json.layers.map((l, i) => ({
          id: `layer_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 5)}`,
          name: String(l.name ?? `Layer ${i + 1}`),
          color: String(l.color ?? "#888888"),
          min: Number.isFinite(l.min) ? l.min : 0,
          max: Number.isFinite(l.max) ? l.max : 255,
          solid: Boolean(l.solid ?? true),
          combine: sanitizeCombine(l.combine),
        }));
        setLayers(sanitized);
        setActivePresetId(null);
        toast.success("Loaded shared palette from link");
      }
    } catch (e) {
      // ignore malformed share URL
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lut = useMemo(
    () => buildLUT(layers, mode === "advanced" ? "exact" : mode === "slope" ? "gradient" : mode, allSolid),
    [layers, mode, allSolid],
  );
  const layerOwnerIndices = useMemo(() => buildLayerIndexLUT(layers), [layers]);
  const combinedMaterials = useMemo(
    () => getCombinedMaterialDescriptors(layers),
    [layers],
  );
  const slopeAngles = useMemo(
    () => computeSlopeAngles(sourceImageDataRef.current),
    // imageVersion changes whenever the source map changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageVersion],
  );
  const slopeMaterial =
    layers.find((layer) => layer.id === slopeSettings.materialId) ??
    layers.find((layer) => layer.name.toLowerCase() === "rock") ??
    layers[0];
  const protectedSlopeMaterialIds = useMemo(() => {
    const ids = Array.isArray(slopeSettings.protectedMaterialIds)
      ? slopeSettings.protectedMaterialIds
      : slopeSettings.protectedMaterialId
        ? [slopeSettings.protectedMaterialId]
        : [];
    return [...new Set(ids)].filter((id) => layers.some((layer) => layer.id === id));
  }, [layers, slopeSettings.protectedMaterialId, slopeSettings.protectedMaterialIds]);
  const protectedSlopeLayerIndices = useMemo(
    () => protectedSlopeMaterialIds
      .map((id) => layers.findIndex((layer) => layer.id === id))
      .filter((index) => index >= 0),
    [layers, protectedSlopeMaterialIds],
  );

  // Debounced layer undo snapshotting — coalesces rapid slider drags.
  useEffect(() => {
    if (initialLoadRef.current) {
      prevLayersRef.current = layers;
      initialLoadRef.current = false;
      return;
    }
    if (layerDebounceRef.current) clearTimeout(layerDebounceRef.current);
    const prev = prevLayersRef.current;
    layerDebounceRef.current = setTimeout(() => {
      if (prev && prev !== layers) {
        layersUndo.push({
          layers: prev,
          allSolid,
          activePresetId,
        });
        prevLayersRef.current = layers;
        setUndoTick((t) => t + 1);
      }
    }, 220);
    return () => {
      if (layerDebounceRef.current) clearTimeout(layerDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers]);

  // Draw the source into the original canvas whenever it's mounted.
  useEffect(() => {
    const img = pendingImageRef.current;
    const c = originalCanvasRef.current;
    if (!img || !c) return;
    c.width = img.width;
    c.height = img.height;
    c.getContext("2d").drawImage(img, 0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasImage, imageVersion, mode]);

  useEffect(() => {
    const src = sourceImageDataRef.current;
    if (!src) return;
    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      let out = renderColorMap(src, lut, combinedMaterials, layerOwnerIndices);
      if (mode === "slope" && slopeMaterial) {
        out = applySlopeMaterial(out, slopeAngles, {
          angle: slopeSettings.angle,
          comparison: slopeSettings.comparison,
          color: slopeMaterial.color,
          sourceImageData: src,
          ownerLayerIndices: layerOwnerIndices,
          protectedOwnerLayerIndices: protectedSlopeLayerIndices,
        });
      }
      setBaseImageData(out);
      const canvas = colorCanvasRef.current;
      if (canvas) {
        canvas.width = src.width;
        canvas.height = src.height;
        const ctx = canvas.getContext("2d");
        ctx.putImageData(out, 0, 0);
        // Exact-Paint strokes live on a locked overlay that shows across every
        // mode and is never altered by layer / slider edits.
        if (
          paintOverlay &&
          paintOverlay.width === src.width &&
          paintOverlay.height === src.height
        ) {
          ctx.drawImage(imageDataToCanvas(paintOverlay), 0, 0);
        }
        // Optional reference layer: the original grayscale height map drawn
        // semi-transparently on top of the color map for alignment.
        if (showReference) {
          ctx.save();
          ctx.globalAlpha = referenceOpacity;
          ctx.drawImage(imageDataToCanvas(src), 0, 0);
          ctx.restore();
        }
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [lut, combinedMaterials, layerOwnerIndices, imageVersion, mode, paintOverlay, protectedSlopeLayerIndices, showReference, referenceOpacity, slopeAngles, slopeMaterial, slopeSettings.angle, slopeSettings.comparison]);

  const mapCoverage = useMemo(
    () => computeCoverage(layers, histogram),
    [layers, histogram],
  );
  const layerCoverage = useMemo(
    () => computeLayerPercentages(layers),
    [layers],
  );

  /* Legacy image loader kept disabled while the paired-map import uses the shared reader below.
  const legacyHandleFile = useCallback((file) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      pendingImageRef.current = img;
      const off = document.createElement("canvas");
      off.width = img.width;
      off.height = img.height;
      const octx = off.getContext("2d");
      octx.drawImage(img, 0, 0);
      const data = octx.getImageData(0, 0, img.width, img.height);
      sourceImageDataRef.current = data;
      setHistogram(computeHistogram(data));
      setImageMeta({ width: img.width, height: img.height });
      setHasImage(true);
      setImageVersion((v) => v + 1);
      paintUndo.clear();
      setUndoTick((t) => t + 1);
      URL.revokeObjectURL(url);
      toast.success(`Loaded ${img.width}×${img.height} image`);
    };
    img.onerror = () => {
      toast.error("Could not read that image.");
      URL.revokeObjectURL(url);
    };
    img.src = url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  */

  const handleFile = useCallback(async (file) => {
    try {
      const heightMap = await readImageFile(file);
      pendingImageRef.current = heightMap.image;
      sourceImageDataRef.current = heightMap.data;
      setHistogram(computeHistogram(heightMap.data));
      setImageMeta({ width: heightMap.width, height: heightMap.height });
      setExactColorImageData(null);
      setTerrainColorImageData(null);
      setPaintOverlay(null);
      setPaintSyncToken((t) => t + 1);
      setHasImage(true);
      setImageVersion((version) => version + 1);
      paintUndo.clear();
      setUndoTick((tick) => tick + 1);
      toast.success(`Loaded ${heightMap.width}×${heightMap.height} height map`);
    } catch {
      toast.error("Could not read that image.");
    }
  }, [paintUndo]);

  const importMatchedColorMap = async (heightFile, colorFile) => {
    try {
      const [heightMap, colorMap] = await Promise.all([
        readImageFile(heightFile),
        readImageFile(colorFile),
      ]);
      if (heightMap.width !== colorMap.width || heightMap.height !== colorMap.height) {
        toast.error(
          `The maps must match exactly. Height map: ${heightMap.width}×${heightMap.height}; color map: ${colorMap.width}×${colorMap.height}.`,
        );
        return false;
      }
      pendingImageRef.current = heightMap.image;
      sourceImageDataRef.current = heightMap.data;
      setHistogram(computeHistogram(heightMap.data));
      setImageMeta({ width: heightMap.width, height: heightMap.height });
      setExactColorImageData(colorMap.data);
      setTerrainColorImageData(colorMap.data);
      setPaintOverlay(null);
      setPaintSyncToken((t) => t + 1);
      setHasImage(true);
      setImageVersion((version) => version + 1);
      paintUndo.clear();
      setUndoTick((tick) => tick + 1);
      setMode("exact");
      toast.success(`Imported matched ${heightMap.width}×${heightMap.height} height and color maps`);
      return true;
    } catch {
      toast.error("Could not read one of those image files.");
      return false;
    }
  };

  const updateLayer = (idx, next) => {
    setLayers((prev) => prev.map((l, i) => (i === idx ? next : l)));
    setActivePresetId(null);
  };
  const deleteLayer = (idx) => {
    if (layers.length <= 1) {
      toast.error("Keep at least one layer.");
      return;
    }
    setLayers((prev) => prev.filter((_, i) => i !== idx));
    setActivePresetId(null);
  };
  const addLayer = () => {
    const newLayer = {
      id: `layer_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: `Layer ${layers.length + 1}`,
      color: "#8a8a8a",
      min: 0,
      max: 32,
      solid: allSolid ? true : false,
    };
    setLayers((prev) => [...prev, newLayer]);
    setActivePresetId(null);
  };
  const reorderLayer = (from, to) => {
    if (from === to || from < 0 || to < 0) return;
    setLayers((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setActivePresetId(null);
  };

  const download = (format = "png") => {
    const canvas =
      mode === "exact" && paintCanvasRef.current
        ? paintCanvasRef.current
        : colorCanvasRef.current;
    if (!canvas || !hasImage) {
      toast.error("Upload a height map first.");
      return;
    }
    const types = {
      png: { mime: "image/png", ext: "png", quality: undefined },
      jpeg: { mime: "image/jpeg", ext: "jpg", quality: 0.95 },
      webp: { mime: "image/webp", ext: "webp", quality: 0.95 },
    };
    const t = types[format] || types.png;
    // JPEG has no alpha, so flatten transparent pixels onto a dark background.
    let out = canvas;
    if (format === "jpeg") {
      const flat = document.createElement("canvas");
      flat.width = canvas.width;
      flat.height = canvas.height;
      const fctx = flat.getContext("2d");
      fctx.fillStyle = "#0b0c10";
      fctx.fillRect(0, 0, flat.width, flat.height);
      fctx.drawImage(canvas, 0, 0);
      out = flat;
    }
    out.toBlob(
      (blob) => {
        if (!blob) {
          toast.error(`This browser can't export ${format.toUpperCase()}.`);
          return;
        }
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `color-map-${imageMeta.width}x${imageMeta.height}.${t.ext}`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(`Color map downloaded (${t.ext.toUpperCase()})`);
      },
      t.mime,
      t.quality,
    );
  };

  const loadSample = (kind) => {
    const canvas = generateSampleHeightMap(kind, 512);
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("Could not generate sample.");
        return;
      }
      const file = new File([blob], `sample-${kind}.png`, { type: "image/png" });
      handleFile(file);
    }, "image/png");
  };


  const resetAll = () => {
    pendingImageRef.current = null;
    sourceImageDataRef.current = null;
    setHasImage(false);
    setImageMeta(null);
    setHistogram(new Uint32Array(256));
    setBaseImageData(null);
    setExactColorImageData(null);
    setTerrainColorImageData(null);
    setPaintOverlay(null);
    setPaintSyncToken((t) => t + 1);
    paintUndo.clear();
    setActivePaintLayerId(null);
    setLayers(buildDefaultLayers());
    setActivePresetId(ROBLOX_PRESET_ID);
    setAllSolid(true);
    setMode("gradient");
    setShowReference(false);
    setReferenceOpacity(0.4);
    setSlopeSettings(DEFAULT_SLOPE_SETTINGS);
    setImageVersion((v) => v + 1);
    setUndoTick((t) => t + 1);
    toast.success("Reset to defaults");
  };


  const exportPalette = () => {
    const payload = { name: "Custom Palette", layers };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `palette-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Palette exported");
  };

  const importPalette = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const arr = Array.isArray(parsed) ? parsed : parsed.layers;
        if (!Array.isArray(arr)) throw new Error("Invalid palette JSON");
        const sanitized = arr.map((l, i) => ({
          id: `layer_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 5)}`,
          name: String(l.name ?? `Layer ${i + 1}`),
          color: String(l.color ?? "#888888"),
          min: Number.isFinite(l.min) ? l.min : 0,
          max: Number.isFinite(l.max) ? l.max : 255,
          solid: Boolean(l.solid ?? true),
          combine: sanitizeCombine(l.combine),
        }));
        setLayers(sanitized);
        setActivePresetId(null);
        toast.success(`Imported ${sanitized.length} layers`);
      } catch (e) {
        toast.error("That file wasn't a valid palette JSON.");
      }
    };
    reader.readAsText(file);
  };

  const availablePresets = useMemo(() => {
    const roblox = {
      id: ROBLOX_PRESET_ID,
      name: "Roblox Terrain",
      builtIn: true,
      description:
        "The required color values for creating custom heatmap materials in Roblox Studio.",
      layers: buildDefaultLayers(),
    };
    return [roblox, ...presets];
  }, [presets]);

  const applyPreset = (p) => {
    setLayers(
      p.layers.map((l, i) => ({
        ...l,
        id: `layer_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 5)}`,
      })),
    );
    setActivePresetId(p.id);
  };
  const saveCurrentPreset = (name, description) => {
    const p = {
      id: `preset_${Date.now()}`,
      name,
      description: description || "",
      builtIn: false,
      createdAt: new Date().toISOString(),
      layers: layers.map((l) => ({ ...l })),
    };
    setPresets((prev) => [...prev, p]);
    setActivePresetId(p.id);
  };
  const deletePreset = (id) => {
    setPresets((prev) => prev.filter((p) => p.id !== id));
    if (activePresetId === id) setActivePresetId(null);
  };
  const setAllSolidWithLayers = (v) => {
    setAllSolid(v);
    // Symmetric: turning All Solid ON forces every layer solid; turning it OFF
    // flips every layer to non-solid so the colors blend (gradient) again.
    setLayers((prev) => prev.map((l) => ({ ...l, solid: v })));
  };
  const addCombinedLayer = (spec, targetLayerId = null) => {
    if (targetLayerId) {
      setLayers((prev) =>
        prev.map((layer) =>
          layer.id === targetLayerId
            ? { ...layer, ...spec, id: layer.id, solid: true }
            : layer,
        ),
      );
      setActivePresetId(null);
      return;
    }
    const newLayer = {
      id: `layer_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      ...spec,
      solid: true,
    };
    setLayers((prev) => [newLayer, ...prev]);
    setActivePresetId(null);
  };

  // Share current palette as a URL
  const sharePalette = async () => {
    const payload = {
      layers: layers.map((l) => ({
        name: l.name,
        color: l.color,
        min: l.min,
        max: l.max,
        solid: l.solid,
        combine: l.combine,
      })),
    };
    const encoded = btoa(JSON.stringify(payload));
    const url = `${window.location.origin}${window.location.pathname}?p=${encoded}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard");
    } catch {
      window.prompt("Copy this share link:", url);
    }
  };

  // Paint undo snapshot handler passed to PaintCanvas
  const onPaintBeforeStroke = useCallback(
    (snap) => {
      paintUndo.push(snap);
      setUndoTick((t) => t + 1);
    },
    [paintUndo],
  );

  const undo = useCallback(() => {
    if (mode === "exact") {
      const snap = paintUndo.pop();
      if (snap) {
        setPaintOverlay(snap);
        setPaintSyncToken((t) => t + 1);
        setUndoTick((t) => t + 1);
        toast.info("Paint undone");
        return;
      }
    }
    const snap = layersUndo.pop();
    if (snap) {
      setLayers(snap.layers);
      setAllSolid(snap.allSolid);
      setActivePresetId(snap.activePresetId);
      prevLayersRef.current = snap.layers;
      setUndoTick((t) => t + 1);
      toast.info("Undone");
    } else {
      toast.error("Nothing to undo");
    }
  }, [mode, layersUndo, paintUndo]);

  useKeyboardUndo(undo);

  const canUndo =
    (mode === "exact" && paintUndo.size() > 0) || layersUndo.size() > 0;

  const showRobloxWarning = activePresetId === ROBLOX_PRESET_ID;

  const selectMode = useCallback((nextMode) => {
    if (nextMode === "advanced" && mode !== "advanced" && !advancedNoticeAcknowledged) {
      setShowAdvancedNotice(true);
      return;
    }
    setMode(nextMode);
  }, [advancedNoticeAcknowledged, mode]);

  const continueToAdvanced = () => {
    setAdvancedNoticeAcknowledged(true);
    setShowAdvancedNotice(false);
    setMode("advanced");
  };

  const open3D = () => {
    if (!hasImage) {
      toast.error("Upload a height map first.");
      return;
    }
    const activeCanvas = mode === "exact" ? paintCanvasRef.current : colorCanvasRef.current;
    if (activeCanvas) {
      setTerrainColorImageData(
        activeCanvas
          .getContext("2d")
          .getImageData(0, 0, activeCanvas.width, activeCanvas.height),
      );
    } else {
      setTerrainColorImageData(mode === "exact" ? exactColorImageData ?? baseImageData : baseImageData);
    }
    setShow3D(true);
  };

  return (
    <>
      {!settings.originalUI ? (
        <PhotoshopLayout
          mode={mode}
          setMode={selectMode}
          hasImage={hasImage}
          imageMeta={imageMeta}
          handleFile={handleFile}
          loadSample={loadSample}
          resetAll={resetAll}
          layers={layers}
          updateLayer={updateLayer}
          deleteLayer={deleteLayer}
          addLayer={addLayer}
          reorderLayer={reorderLayer}
          dragIndex={dragIndex}
          setDragIndex={setDragIndex}
          allSolid={allSolid}
          setAllSolidWithLayers={setAllSolidWithLayers}
          mapCoverage={mapCoverage}
          layerCoverage={layerCoverage}
          histogram={histogram}
          metric={metric}
          setMetric={setMetric}
          originalCanvasRef={originalCanvasRef}
          colorCanvasRef={colorCanvasRef}
          paintCanvasRef={paintCanvasRef}
          baseImageData={baseImageData}
          exactColorImageData={exactColorImageData}
          paintOverlay={paintOverlay}
          setPaintOverlay={setPaintOverlay}
          paintSyncToken={paintSyncToken}
          activePaintLayerId={activePaintLayerId}
          setActivePaintLayerId={setActivePaintLayerId}
          imageVersion={imageVersion}
          showReference={showReference}
          setShowReference={setShowReference}
          referenceOpacity={referenceOpacity}
          setReferenceOpacity={setReferenceOpacity}
          sourceImageData={sourceImageDataRef.current}
          undo={undo}
          canUndo={canUndo}
          onPaintBeforeStroke={onPaintBeforeStroke}
          sharePalette={sharePalette}
          exportPalette={exportPalette}
          importPalette={importPalette}
          importInputRef={importInputRef}
          open3D={open3D}
          setShowPresets={setShowPresets}
          setShowSettings={setShowSettings}
          setShowHelp={setShowHelp}
          setShowColorMapImport={setShowColorMapImport}
          availablePresets={availablePresets}
          activePresetId={activePresetId}
          download={download}
          settings={settings}
          showRobloxWarning={showRobloxWarning}
          ROBLOX_PRESET_ID={ROBLOX_PRESET_ID}
          customPatterns={customPatterns}
          addCombinedLayer={addCombinedLayer}
          slopeAngles={slopeAngles}
          slopeSettings={slopeSettings}
          setSlopeSettings={setSlopeSettings}
          ownerLayerIndices={layerOwnerIndices}
        />
      ) : (
        <div className="min-h-screen bg-[#0b0c10] relative overflow-x-hidden">
          <div
            data-testid="hero-halo"
            className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full brand-gradient opacity-20 blur-3xl"
          />
          <div className="pointer-events-none absolute -top-40 right-0 w-[500px] h-[500px] rounded-full bg-orange-500/5 blur-3xl" />

          <div className="relative max-w-[1400px] mx-auto px-6 py-6 space-y-5">
            <header className="flex items-center justify-between">
              <button
                type="button"
                data-testid="logo-reset-btn"
                onClick={resetAll}
                title="Reset everything to defaults"
                className="flex items-center gap-3 group text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
              >
                <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center shadow-lg shadow-rose-500/20 transition-transform duration-200 group-hover:scale-105 group-active:scale-95">
                  <Layers className="w-5 h-5 text-white" strokeWidth={2.3} />
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight text-zinc-100">
                    Height Map <span className="text-rose-400">Color Map Generator</span>
                  </h1>
                  <p className="text-xs text-zinc-500 -mt-0.5">
                    Grayscale terrain height maps &mdash; rich color maps.
                  </p>
                </div>
              </button>
              <div className="flex items-center gap-2">
                <button
                  data-testid="undo-btn"
                  onClick={undo}
                  disabled={!canUndo}
                  title="Undo (Ctrl/Cmd+Z)"
                  className={`h-10 px-3 rounded-xl border flex items-center gap-1.5 text-xs font-medium transition ${
                    canUndo
                      ? "bg-zinc-900/70 hover:bg-zinc-800 border-zinc-800 text-zinc-200"
                      : "bg-zinc-900/40 border-zinc-800/50 text-zinc-600 cursor-not-allowed"
                  }`}
                >
                  <Undo2 className="w-3.5 h-3.5" />
                  Undo
                </button>
                <button
                  data-testid="share-btn"
                  onClick={sharePalette}
                  title="Copy shareable palette link"
                  className="h-10 px-3 rounded-xl bg-zinc-900/70 hover:bg-zinc-800 border border-zinc-800 flex items-center gap-1.5 text-xs font-medium text-zinc-200 transition"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Share
                </button>
                <button
                  data-testid="3d-btn"
                  onClick={open3D}
                  title="3D Terrain Peek"
                  className="h-10 px-3 rounded-xl bg-zinc-900/70 hover:bg-zinc-800 border border-zinc-800 flex items-center gap-1.5 text-xs font-medium text-zinc-200 transition"
                >
                  <Box className="w-3.5 h-3.5" />
                  3D
                </button>
                <button
                  data-testid="settings-btn"
                  onClick={() => setShowSettings(true)}
                  className="w-10 h-10 rounded-xl bg-zinc-900/70 hover:bg-zinc-800 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-zinc-100 transition"
                  aria-label="Settings"
                >
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            </header>

            <div className="relative z-40 glass rounded-2xl p-2 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex bg-[#0d0e12] rounded-xl p-1 border border-zinc-800/70">
                  <ModeTab testId="mode-gradient" active={mode === "gradient"} onClick={() => selectMode("gradient")} icon={<Palette className="w-3.5 h-3.5" />} label="Elevation" />
                  <ModeTab testId="mode-exact" active={mode === "exact"} onClick={() => selectMode("exact")} icon={<Sliders className="w-3.5 h-3.5" />} label="Manual Paint" />
                  <ModeTab testId="mode-advanced" active={mode === "advanced"} onClick={() => selectMode("advanced")} icon={<Sparkles className="w-3.5 h-3.5" />} label="Material Mix" />
                  <ModeTab testId="mode-slope" active={mode === "slope"} onClick={() => selectMode("slope")} icon={<Mountain className="w-3.5 h-3.5" />} label="Steep Slopes" />
                </div>
                <ReferenceControl
                  enabled={showReference}
                  opacity={referenceOpacity}
                  disabled={!hasImage}
                  onToggle={() => setShowReference((v) => !v)}
                  onOpacityChange={setReferenceOpacity}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="presets-btn"
                  variant="ghost"
                  onClick={() => setShowPresets(true)}
                  className="accent-border-hover bg-zinc-900/60 hover:bg-zinc-800 border border-zinc-800 text-zinc-200"
                >
                  <Palette className="w-4 h-4 mr-1.5" />
                  Presets
                  {activePresetId === ROBLOX_PRESET_ID && (
                    <span data-testid="active-preset-status" className="ml-1 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-300">
                      Roblox Terrain loaded
                    </span>
                  )}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="download-btn"
                      disabled={!hasImage}
                      className="brand-gradient text-white font-semibold hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4 mr-1.5" />
                      Download Color Map
                      <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-80" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    data-testid="download-format-menu"
                    align="end"
                    className="bg-[#141519] border-zinc-700 text-zinc-100"
                  >
                    <DropdownMenuLabel className="text-zinc-500 text-[10px] uppercase tracking-widest">
                      Download as
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      data-testid="download-png"
                      onClick={() => download("png")}
                      className="group cursor-pointer focus:bg-rose-500/15 focus:text-white"
                    >
                      PNG
                      <span className="ml-auto text-[10px]">
                        <span className="text-zinc-500 group-hover:hidden group-focus:hidden">
                          transparent
                        </span>
                        <span
                          data-testid="download-png-recommended"
                          className="hidden font-semibold text-rose-300 group-hover:inline group-focus:inline"
                        >
                          Recommended
                        </span>
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="download-jpeg"
                      onClick={() => download("jpeg")}
                      className="cursor-pointer focus:bg-rose-500/15 focus:text-white"
                    >
                      JPG <span className="ml-auto text-[10px] text-zinc-500">smaller</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="download-webp"
                      onClick={() => download("webp")}
                      className="cursor-pointer focus:bg-rose-500/15 focus:text-white"
                    >
                      WEBP <span className="ml-auto text-[10px] text-zinc-500">efficient</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {settings.showImportColorMap && (
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          data-testid="import-color-map-btn"
                          variant="ghost"
                          onClick={() => setShowColorMapImport(true)}
                          className="relative bg-zinc-900/60 hover:bg-zinc-800 border border-zinc-800 text-zinc-200"
                        >
                          <Upload className="w-4 h-4 mr-1.5" />
                          Import Color Map
                          <span
                            data-testid="import-color-map-experimental-badge"
                            className="ml-1.5 rounded-full border border-amber-400/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-300"
                          >
                            Experimental
                          </span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        data-testid="import-color-map-experimental-tooltip"
                        className="bg-[#141519] border-zinc-700 text-amber-200 text-xs"
                      >
                        Experimental
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>

            {(mode === "gradient" || mode === "slope") && (
              <DropZone
                onFile={handleFile}
                hasImage={hasImage}
                imageMeta={imageMeta}
                originalCanvasRef={originalCanvasRef}
                colorCanvasRef={colorCanvasRef}
                bigViewports={settings.bigViewports}
                roundedMapPreview={settings.roundedMapPreview}
                onLoadSample={loadSample}
              />
            )}
            {mode === "slope" && (
              <SlopeControls
                sourceImageData={sourceImageDataRef.current}
                slopeAngles={slopeAngles}
                layers={layers}
                slopeSettings={slopeSettings}
                onChange={setSlopeSettings}
                ownerLayerIndices={layerOwnerIndices}
                roundedMapPreview={settings.roundedMapPreview}
              />
            )}
            {mode === "exact" && !hasImage && (
              <DropZone
                onFile={handleFile}
                hasImage={false}
                imageMeta={null}
                originalCanvasRef={originalCanvasRef}
                colorCanvasRef={colorCanvasRef}
                bigViewports={settings.bigViewports}
                roundedMapPreview={settings.roundedMapPreview}
                onLoadSample={loadSample}
              />
            )}
            {mode === "exact" && hasImage && (
              <>
                <canvas ref={colorCanvasRef} className="hidden" />
                <PaintCanvas
                  baseImageData={exactColorImageData ?? baseImageData}
                  paintCanvasRef={paintCanvasRef}
                  layers={layers}
                  activeLayerId={activePaintLayerId || layers[0]?.id}
                  onSelectLayer={setActivePaintLayerId}
                  paintKey={imageVersion}
                  paintOverlay={paintOverlay}
                  paintSyncToken={paintSyncToken}
                  onOverlayChange={setPaintOverlay}
                  referenceImageData={sourceImageDataRef.current}
                  showReference={showReference}
                  referenceOpacity={referenceOpacity}
                  onBeforeStroke={onPaintBeforeStroke}
                  onUndo={undo}
                  canUndo={canUndo}
                  viewportHeight={settings.bigViewports ? 720 : 560}
                  roundedMapPreview={settings.roundedMapPreview}
                />
              </>
            )}
            {mode === "advanced" && !hasImage && (
              <DropZone
                onFile={handleFile}
                hasImage={false}
                imageMeta={null}
                originalCanvasRef={originalCanvasRef}
                colorCanvasRef={colorCanvasRef}
                bigViewports={settings.bigViewports}
                roundedMapPreview={settings.roundedMapPreview}
                onLoadSample={loadSample}
              />
            )}
            {mode === "advanced" && hasImage && (
              <>
                <canvas ref={colorCanvasRef} className="hidden" />
                <AdvancedMode
                  sourceImageData={sourceImageDataRef.current}
                  layers={layers}
                  mode="exact"
                  allSolid={allSolid}
                  bigViewports={settings.bigViewports}
                  paintOverlay={paintOverlay}
                  showReference={showReference}
                  referenceOpacity={referenceOpacity}
                  customPatterns={customPatterns}
                  onSaveCombinedLayer={addCombinedLayer}
                  roundedMapPreview={settings.roundedMapPreview}
                />
              </>
            )}

            <div
              className={`grid gap-5 ${
                settings.showHow ? "grid-cols-1 lg:grid-cols-[1fr_360px]" : "grid-cols-1"
              }`}
            >
              <div className="rounded-2xl border border-zinc-800/80 bg-[#101116] p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-rose-400" />
                    <span className="text-zinc-100 font-semibold">Height Layers</span>
                    <span data-testid="layer-count" className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 mono">
                      {layers.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <IconBtn testId="import-palette-btn" title="Import palette JSON" onClick={() => importInputRef.current?.click()}>
                      <Upload className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn testId="export-palette-btn" title="Export palette JSON" onClick={exportPalette}>
                      <Download className="w-4 h-4" />
                    </IconBtn>
                    <input
                      ref={importInputRef}
                      type="file"
                      accept="application/json"
                      hidden
                      data-testid="import-palette-input"
                      onChange={(e) => {
                        if (e.target.files?.[0]) importPalette(e.target.files[0]);
                        e.target.value = "";
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-xl bg-[#0d0e12] border border-zinc-800/70 px-4 py-2.5">
                  <span
                    data-testid="all-solid-label"
                    className={`text-sm font-medium ${allSolid ? "text-zinc-100" : "text-zinc-400"}`}
                  >
                    All Solid Colors {allSolid ? "On" : "Off"}
                  </span>
                  <Switch data-testid="all-solid-toggle" checked={allSolid} onCheckedChange={setAllSolidWithLayers} />
                </div>

                {settings.showStrip && (
                  <PaletteStrip
                    layers={layers}
                    mapCoverage={mapCoverage}
                    layerCoverage={layerCoverage}
                    histogram={hasImage ? histogram : null}
                    metric={metric}
                    onMetricChange={setMetric}
                  />
                )}

                <div
                  className="min-w-0 space-y-3 overflow-y-auto overflow-x-hidden scroll-thin pr-2"
                  style={{ maxHeight: settings.layerListHeight }}
                >
                  {layers.map((layer, idx) => (
                    <HeightLayerRow
                      key={layer.id}
                      layer={layer}
                      layerPercentage={layerCoverage[idx] ?? 0}
                      mapPercentage={mapCoverage[idx] ?? 0}
                      hasImage={hasImage}
                      disableSolidToggle={allSolid}
                      showRobloxWaterWarning={
                        showRobloxWarning && layer.name.toLowerCase() === "water"
                      }
                      onChange={(next) => updateLayer(idx, next)}
                      onDelete={() => deleteLayer(idx)}
                      isDragging={dragIndex === idx}
                      onDragStart={(e) => {
                        setDragIndex(idx);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragIndex !== null) reorderLayer(dragIndex, idx);
                        setDragIndex(null);
                      }}
                      onDragEnd={() => setDragIndex(null)}
                    />
                  ))}
                </div>

                <button
                  data-testid="add-layer-btn"
                  onClick={addLayer}
                  className="accent-border-hover w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-zinc-700 text-zinc-400 hover:text-zinc-100 transition-all duration-200 ease-out hover:-translate-y-px hover:shadow-lg hover:shadow-rose-500/10 active:translate-y-0 active:scale-[0.99]"
                >
                  <Plus className="w-4 h-4" />
                  Add height layer
                </button>
              </div>

              {settings.showHow && (
                <HowItWorks
                  onOpenHelp={() => setShowHelp(true)}
                  showRobloxWarning={showRobloxWarning}
                />
              )}
            </div>

            <footer
              data-testid="site-footer"
              className="mt-2 rounded-2xl border border-zinc-800/80 bg-[#101116] px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl brand-gradient flex items-center justify-center shadow-lg shadow-rose-500/20">
                  <Layers className="w-4 h-4 text-white" strokeWidth={2.3} />
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold text-zinc-100">
                    A product of Polaris Games
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    Built for terrain artists · everything stays in your browser
                  </div>
                </div>
              </div>
              <a
                data-testid="roblox-group-link"
                href="https://www.roblox.com/share/g/751583715"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl brand-gradient px-4 py-2 text-sm font-semibold text-white shadow-md shadow-rose-500/25 transition-all duration-200 ease-out hover:-translate-y-px hover:shadow-lg hover:shadow-rose-500/35 active:translate-y-0"
              >
                <Users className="w-4 h-4" />
                Join our Roblox Group
              </a>
            </footer>
          </div>
        </div>
      )}

      <HelpDialog open={showHelp} onOpenChange={setShowHelp} />
      <PresetsDialog
        open={showPresets}
        onOpenChange={setShowPresets}
        presets={availablePresets}
        activePresetId={activePresetId}
        onApply={applyPreset}
        onSaveCurrent={saveCurrentPreset}
        onDelete={deletePreset}
      />
      <SettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        settings={settings}
        onChange={setSettings}
        customPatterns={customPatterns}
        onSavePattern={savePattern}
        onDeletePattern={deletePattern}
      />
      <ColorMapImportDialog
        open={showColorMapImport}
        onOpenChange={setShowColorMapImport}
        onImport={importMatchedColorMap}
      />
      <AdvancedModeNotice
        open={showAdvancedNotice}
        onOpenChange={setShowAdvancedNotice}
        onContinue={continueToAdvanced}
      />
      <Terrain3D
        open={show3D}
        onOpenChange={setShow3D}
        sourceImageData={sourceImageDataRef.current}
        colorImageData={terrainColorImageData ?? (mode === "exact" ? exactColorImageData ?? baseImageData : baseImageData)}
      />
    </>
  );
};

const ReferenceControl = ({ enabled, opacity, disabled, onToggle, onOpacityChange }) => (
  <div className="relative group">
    <button
      data-testid="reference-toggle"
      disabled={disabled}
      onClick={onToggle}
      title={disabled ? "Upload a height map first" : "Overlay the original height map"}
      className={`flex items-center gap-1.5 h-9 px-3 rounded-xl border text-xs font-medium transition-all duration-200 ease-out ${
        disabled
          ? "border-zinc-800/60 bg-zinc-900/40 text-zinc-600 cursor-not-allowed"
          : enabled
            ? "brand-gradient text-white border-transparent shadow-md shadow-rose-500/25"
            : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:text-white hover:border-zinc-700"
      }`}
    >
      <Eye className="w-3.5 h-3.5" />
      Reference
      <span
        className={`ml-0.5 text-[9px] font-bold uppercase ${
          enabled ? "text-white/90" : "text-zinc-500"
        }`}
      >
        {enabled ? "On" : "Off"}
      </span>
    </button>
    {!disabled && (
      <div className="absolute left-0 top-full pt-2 z-40 opacity-0 translate-y-1 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:translate-y-0 focus-within:pointer-events-auto transition-all duration-200 ease-out">
        <div className="w-56 rounded-xl border border-zinc-700 bg-[#141519] p-3 shadow-2xl shadow-black/60">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Reference opacity
            </span>
            <span className="mono text-[11px] text-zinc-300">
              {Math.round(opacity * 100)}%
            </span>
          </div>
          <input
            data-testid="reference-opacity"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={opacity}
            onChange={(e) => onOpacityChange(Number(e.target.value))}
            className="w-full accent-rose-500 cursor-pointer"
          />
          <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">
            Overlays the original grayscale height map on every color map so you can line features up.
          </p>
        </div>
      </div>
    )}
  </div>
);


const ModeTab = ({ active, onClick, icon, label, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ease-out hover:-translate-y-px active:translate-y-0 active:scale-[0.97] ${
      active
        ? "brand-gradient text-white shadow-md shadow-rose-500/25"
        : "text-zinc-400 hover:bg-zinc-800/70 hover:text-zinc-100"
    }`}
  >
    {icon}
    {label}
  </button>
);

const IconBtn = ({ children, onClick, title, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    title={title}
    className="accent-border-hover w-8 h-8 rounded-lg border border-transparent text-zinc-500 hover:text-zinc-100 flex items-center justify-center transition-all duration-200 ease-out hover:-translate-y-px active:translate-y-0 active:scale-95"
  >
    {children}
  </button>
);

function loadPresets() {
  try {
    const raw = localStorage.getItem(PRESET_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    const normalized = { ...parsed };
    delete normalized.photoshopUI;
    return {
      ...DEFAULT_SETTINGS,
      ...normalized,
      originalUI: normalized.originalUI === true,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function loadPatterns() {
  try {
    const raw = localStorage.getItem(PATTERN_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function sanitizeCombine(combine) {
  if (!combine || !Array.isArray(combine.materials)) return undefined;
  const materials = combine.materials
    .filter((material) => material && typeof material.color === "string")
    .map((material) => ({
      color: material.color,
      weight: Number.isFinite(material.weight) ? material.weight : 1,
    }));
  if (materials.length < 2) return undefined;
  const cw = combine.customWidth ?? combine.customSize;
  const ch = combine.customHeight ?? combine.customSize;
  const isCustom =
    combine.pattern === "custom" &&
    Array.isArray(combine.customCells) &&
    Number.isFinite(cw) &&
    Number.isFinite(ch);
  return {
    materials,
    pattern: isCustom
      ? "custom"
      : ["noise", "checker", "stripes", "dots"].includes(combine.pattern)
        ? combine.pattern
        : "noise",
    ...(isCustom
      ? {
          customCells: combine.customCells.map(Boolean),
          customWidth: cw,
          customHeight: ch,
        }
      : {}),
    scale: Number.isFinite(combine.scale) ? combine.scale : 6,
    intensity: Number.isFinite(combine.intensity) ? combine.intensity : 0.5,
    rotation: Number.isFinite(combine.rotation) ? combine.rotation : 0,
  };
}

function readImageFile(file) {
  return new Promise((resolve, reject) => {
    if (!file?.type?.startsWith("image/")) {
      reject(new Error("Not an image"));
      return;
    }
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.width;
      canvas.height = image.height;
      canvas.getContext("2d").drawImage(image, 0, 0);
      const data = canvas.getContext("2d").getImageData(0, 0, image.width, image.height);
      URL.revokeObjectURL(url);
      resolve({ image, data, width: image.width, height: image.height });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Unreadable image"));
    };
    image.src = url;
  });
}

export default HeightMapGenerator;
